import { EnemyType, EnemyGrade, DIFFICULTY_POWER_OFFSET } from '@/config/GameConfig';
import { enemiesForTier } from '@/config/DifficultyScaling';
import { notifyProfileChanged } from '@/config/ProfileSync';

// ── Power values: enemy type × grade ──
// Represents the "threat" of a single enemy instance.

// Default power costs per enemy type (Normal grade). Grades scale as multiples of base.
// These can be overridden at runtime via EnemyTuningScene.
const DEFAULT_BASE_POWER: Record<EnemyType, number> = {
  [EnemyType.GRUNT]:   1.0,
  [EnemyType.RUNNER]:  1.0,
  [EnemyType.SWARM]:   0.5,
  [EnemyType.TANK]:    5.0,
  [EnemyType.PHANTOM]: 2.5,
  [EnemyType.DRONE]:   2.0,
  [EnemyType.WASP]:    1.5,
  [EnemyType.BRUTE]:   8.0,
  [EnemyType.BOMBER]:  4.0,
  [EnemyType.SPECTER]: 3.0,
  [EnemyType.HIVE]:    6.0,
  [EnemyType.HEALER]:  3.5,
};

// Grade multipliers relative to Normal base power
const GRADE_POWER_MULT: Record<EnemyGrade, number> = {
  [EnemyGrade.NORMAL]: 1,
  [EnemyGrade.ELITE]:  2,
  [EnemyGrade.BOSS]:   3,
  [EnemyGrade.SUPER]:  4,
  [EnemyGrade.ULTRA]:  6,
  [EnemyGrade.GOLD]:   10,
};

/** Live base power values — mutated by EnemyTuningScene overrides */
export const BASE_POWER: Record<EnemyType, number> = { ...DEFAULT_BASE_POWER };

/** Rebuild full POWER table from BASE_POWER × GRADE_POWER_MULT */
function rebuildPowerTable(): void {
  for (const type of Object.values(EnemyType)) {
    const base = BASE_POWER[type] ?? 1;
    for (const grade of Object.values(EnemyGrade)) {
      POWER[type][grade] = base * (GRADE_POWER_MULT[grade] ?? 1);
    }
  }
}

const POWER: Record<EnemyType, Record<EnemyGrade, number>> = {} as any;
// Initialize all entries
for (const type of Object.values(EnemyType)) {
  POWER[type] = {} as Record<EnemyGrade, number>;
}
rebuildPowerTable();

/** Called by EnemyTuningScene when power cost is changed */
export function setBasePower(type: EnemyType, value: number): void {
  BASE_POWER[type] = value;
  rebuildPowerTable();
}

/** Reset all power costs to defaults */
export function resetBasePower(): void {
  Object.assign(BASE_POWER, DEFAULT_BASE_POWER);
  rebuildPowerTable();
}

// ── Spawn weight per enemy type (higher = more frequent in random picks) ──
const TYPE_WEIGHT: Record<EnemyType, number> = {
  [EnemyType.GRUNT]: 4, [EnemyType.RUNNER]: 3, [EnemyType.SWARM]: 4,
  [EnemyType.TANK]: 1.5, [EnemyType.PHANTOM]: 1.5, [EnemyType.DRONE]: 2,
  [EnemyType.WASP]: 3, [EnemyType.BRUTE]: 1, [EnemyType.BOMBER]: 1,
  [EnemyType.SPECTER]: 1,
  [EnemyType.HIVE]: 1.5,
  [EnemyType.HEALER]: 1.5,
};

// ── Grade unlock by tier ──
const GRADE_UNLOCK: { grade: EnemyGrade; minTier: number }[] = [
  { grade: EnemyGrade.ELITE, minTier: 5 },
  { grade: EnemyGrade.BOSS,  minTier: 10 },
  { grade: EnemyGrade.SUPER, minTier: 16 },
  { grade: EnemyGrade.ULTRA, minTier: 22 },
  { grade: EnemyGrade.GOLD,  minTier: 28 },
];

// ── Tunable curve parameters (can be changed at runtime by dev panel) ──
export interface CurveParams {
  basePower: number;    // power for map 1 (default 30)
  growthRate: number;   // exponential growth per map (default 1.06 = 6%)
  waveMin: number;      // minimum waves (default 5)
  waveMax: number;      // maximum waves (default 22)
}

const DEFAULT_CURVE: CurveParams = {
  basePower: 30,
  growthRate: 1.06,
  waveMin: 5,
  waveMax: 22,
};

let activeCurve: CurveParams = { ...DEFAULT_CURVE };

// ── Storage ──
const STORAGE_KEY = 'robogame_power_curve';

export function saveCurve(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(activeCurve));
  notifyProfileChanged();
}

export function loadCurve(): void {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try { Object.assign(activeCurve, JSON.parse(raw)); } catch (e) { console.warn('[WaveComposer] loadCurve parse failed:', e); }
  }
}

export function getCurve(): CurveParams { return activeCurve; }

export function setCurve(params: Partial<CurveParams>): void {
  Object.assign(activeCurve, params);
}

export function resetCurve(): void {
  activeCurve = { ...DEFAULT_CURVE };
}

// ── Core calculations ──

/** Total power budget for map m (1-99), offset by difficulty level */
export function mapPower(m: number): number {
  const base = activeCurve.basePower + DIFFICULTY_POWER_OFFSET;
  return Math.round(base * Math.pow(activeCurve.growthRate, m - 1));
}

/** Number of waves for map m (1-99) */
export function mapWaves(m: number): number {
  const t = (m - 1) / 98; // 0..1
  return Math.round(activeCurve.waveMin + t * (activeCurve.waveMax - activeCurve.waveMin));
}

/** Tier for map m (1-99) → 0-32 */
export function mapTier(m: number): number {
  return Math.floor((m - 1) * 32 / 98);
}

/** Available grades for a given tier */
export function gradesForTier(tier: number): EnemyGrade[] {
  const grades: EnemyGrade[] = [EnemyGrade.NORMAL];
  for (const { grade, minTier } of GRADE_UNLOCK) {
    if (tier >= minTier) grades.push(grade);
  }
  return grades;
}

/** Get power value for a specific enemy type+grade */
export function enemyPower(type: EnemyType, grade: EnemyGrade): number {
  return POWER[type]?.[grade] ?? 1;
}

// ── Wave entry ──
export interface WaveEntry {
  type: EnemyType;
  grade: EnemyGrade;
}

/** Power distribution across waves — earlier waves are lighter, later are heavier */
function wavePowerDistribution(totalPower: number, numWaves: number, flat = false): number[] {
  if (flat) {
    // Equal power per wave (stress test / benchmark)
    const perWave = Math.round(totalPower / numWaves);
    return Array.from({ length: numWaves }, () => perWave);
  }
  // Quadratic ramp: wave i gets weight (i+1)^1.3
  const weights: number[] = [];
  let totalWeight = 0;
  for (let i = 0; i < numWaves; i++) {
    const w = Math.pow(i + 1, 1.3);
    weights.push(w);
    totalWeight += w;
  }
  return weights.map(w => Math.round(totalPower * w / totalWeight));
}

/**
 * Build a single wave's enemy list to fill a given power budget.
 * Uses weighted random selection from allowed types and grades.
 */
export function composeWave(
  budget: number,
  allowedTypes: EnemyType[],
  allowedGrades: EnemyGrade[],
  waveProgress: number, // 0..1 (0 = first wave, 1 = last)
): WaveEntry[] {
  const entries: WaveEntry[] = [];
  let remaining = budget;

  // Build weighted type pool
  const pool = allowedTypes.map(t => ({ type: t, weight: TYPE_WEIGHT[t] ?? 1 }));
  const totalWeight = pool.reduce((s, p) => s + p.weight, 0);

  // Grade probability scales with wave progress
  // Early waves: mostly Normal. Late waves: more special grades.
  const gradeProbs: { grade: EnemyGrade; prob: number }[] = [];
  let normalProb = 1;
  for (const g of allowedGrades) {
    if (g === EnemyGrade.NORMAL) continue;
    // Linear ramp: 0 at wave 0, maxProb at wave 1
    const maxProb = g === EnemyGrade.ELITE ? 0.20
      : g === EnemyGrade.BOSS ? 0.12
      : g === EnemyGrade.SUPER ? 0.10
      : g === EnemyGrade.ULTRA ? 0.06
      : 0.03; // GOLD
    const prob = maxProb * waveProgress;
    gradeProbs.push({ grade: g, prob });
    normalProb -= prob;
  }
  normalProb = Math.max(0.3, normalProb);

  // Safety: minimum 1 enemy per wave
  const maxIterations = 500;
  let iterations = 0;

  while (remaining > 0.1 && iterations < maxIterations) {
    iterations++;

    // Pick type (weighted random)
    let roll = Math.random() * totalWeight;
    let pickedType = pool[0].type;
    for (const p of pool) {
      roll -= p.weight;
      if (roll <= 0) { pickedType = p.type; break; }
    }

    // Pick grade
    let pickedGrade = EnemyGrade.NORMAL;
    let gradeRoll = Math.random();
    let cumulative = 0;
    for (const gp of gradeProbs) {
      cumulative += gp.prob;
      if (gradeRoll < cumulative) { pickedGrade = gp.grade; break; }
    }

    const cost = enemyPower(pickedType, pickedGrade);

    // Don't overshoot budget by more than 50% of a basic enemy
    if (cost > remaining + 0.5) {
      // Try to fit a cheaper enemy instead
      const cheapest = Math.min(...allowedTypes.map(t => POWER[t][EnemyGrade.NORMAL]));
      if (remaining < cheapest * 0.5) break;
      // Re-roll with only Normal grade
      pickedGrade = EnemyGrade.NORMAL;
      const normalCost = POWER[pickedType][EnemyGrade.NORMAL];
      if (normalCost > remaining + 0.5) continue;
      entries.push({ type: pickedType, grade: pickedGrade });
      remaining -= normalCost;
      continue;
    }

    entries.push({ type: pickedType, grade: pickedGrade });
    remaining -= cost;
  }

  // Guarantee at least 1 enemy
  if (entries.length === 0 && allowedTypes.length > 0) {
    entries.push({ type: allowedTypes[0], grade: EnemyGrade.NORMAL });
  }

  // ── Healer diversity fix ──
  // Healers are support units — cap them at ~30% of a wave and ensure
  // they always spawn alongside combat enemies.
  const nonHealerTypes = allowedTypes.filter(t => t !== EnemyType.HEALER);
  if (nonHealerTypes.length > 0) {
    const healerCount = entries.filter(e => e.type === EnemyType.HEALER).length;
    const maxHealers = Math.max(1, Math.floor(entries.length * 0.3));
    if (healerCount > maxHealers) {
      let replaced = 0;
      for (let i = 0; i < entries.length && replaced < healerCount - maxHealers; i++) {
        if (entries[i].type === EnemyType.HEALER) {
          entries[i].type = nonHealerTypes[Math.floor(Math.random() * nonHealerTypes.length)];
          replaced++;
        }
      }
    }
    // If wave has healers but zero combat enemies, swap the first healer
    const hasNonHealer = entries.some(e => e.type !== EnemyType.HEALER);
    if (!hasNonHealer && entries.length > 0) {
      entries[0].type = nonHealerTypes[Math.floor(Math.random() * nonHealerTypes.length)];
    }
  }

  return entries;
}

/**
 * Compose all waves for a given map index (1-99).
 * If overrideWaves is provided, use that wave count instead of the computed one.
 * Returns an array of waves, each wave being a list of WaveEntry.
 */
export function composeMap(mapIndex: number, overrideWaves?: number, flat = false, overrideEnemies?: EnemyType[], intensityMult = 1, overrideTier?: number, overrideGrades?: EnemyGrade[]): WaveEntry[][] {
  const tier = overrideTier ?? mapTier(mapIndex);
  const totalPower = Math.round(mapPower(mapIndex) * intensityMult);
  const numWaves = overrideWaves ?? mapWaves(mapIndex);
  const allowedTypes = overrideEnemies && overrideEnemies.length > 0 ? overrideEnemies : enemiesForTier(tier);
  // Use override grades if provided (map editor), otherwise auto from tier
  const allowedGrades = overrideGrades && overrideGrades.length > 0
    ? [EnemyGrade.NORMAL, ...overrideGrades]
    : gradesForTier(tier);

  const budgets = wavePowerDistribution(totalPower, numWaves, flat);
  const waves: WaveEntry[][] = [];

  for (let w = 0; w < numWaves; w++) {
    const progress = numWaves > 1 ? w / (numWaves - 1) : 0;
    // Wave 1 is always all Normal
    const waveGrades = w === 0 ? [EnemyGrade.NORMAL] : allowedGrades;
    waves.push(composeWave(budgets[w], allowedTypes, waveGrades, progress));
  }

  return waves;
}

/** Summarize a wave: count per type+grade */
export interface WaveSummaryEntry {
  type: EnemyType;
  grade: EnemyGrade;
  count: number;
  power: number;
}

export function summarizeWave(entries: WaveEntry[]): WaveSummaryEntry[] {
  const counts = new Map<string, WaveSummaryEntry>();
  for (const e of entries) {
    const key = `${e.type}_${e.grade}`;
    if (!counts.has(key)) {
      counts.set(key, { type: e.type, grade: e.grade, count: 0, power: 0 });
    }
    const entry = counts.get(key)!;
    entry.count++;
    entry.power += enemyPower(e.type, e.grade);
  }
  // Sort by power descending (heaviest threats first)
  return [...counts.values()].sort((a, b) => b.power - a.power);
}

/** Total power actually spent in a wave */
export function wavePowerActual(entries: WaveEntry[]): number {
  return entries.reduce((s, e) => s + enemyPower(e.type, e.grade), 0);
}

// Initialize from localStorage on module load
loadCurve();
